import { Feature } from '@turf/helpers';

import { TYPE_CITY, TYPE_COUNTRY, TYPE_REGION } from '@/common/components/controls/locationSelector/SelectableNode';
import Site, { ISiteAPI } from '@/common/models/site/Site';

import LocationShape, { ILocationShapeAPI } from './LocationShape';

export interface ILocationAPI {
  location_id: number;
  region_id: number;
  level: number;
  name: string;
  code: string;
  country_id: number;
  subregion_id: number;
  children?: ILocationAPI[];
  sites?: ISiteAPI[];
  search_match?: boolean;
  external_id: string;
  shapes?: ILocationShapeAPI[];
  hierarchy?: string;
  archived?: boolean;
}

export default class Location {
  id: number;

  regionID: number;

  level: number;

  name: string;

  code: string;

  countryID: number;

  subRegionID: number;

  locations: Location[];

  sites: Site[];

  searchMatch: boolean;

  externalID: string;

  shapes: LocationShape[];

  hierarchy: string;

  archived: boolean;

  constructor(
    id: number,
    regionID: number,
    level: number,
    name: string,
    code: string,
    countryID: number,
    subRegionID: number,
    children: Location[],
    sites: Site[],
    searchMatch: boolean,
    externalID: string,
    shapes: LocationShape[],
    hierarchy: string,
    archived: boolean,
  ) {
    this.id = id;
    this.regionID = regionID;
    this.level = level;
    this.name = name;
    this.code = code;
    this.countryID = countryID;
    this.subRegionID = subRegionID;
    this.locations = children;
    this.sites = sites;
    this.searchMatch = searchMatch;
    this.externalID = externalID;
    this.shapes = shapes;
    this.hierarchy = hierarchy;
    this.archived = archived;
  }

  get displayName(): string {
    return this.name;
  }

  get hierarchyNoCountry(): string {
    // Used in OverallMap `option-label-property="hierarchyNoCountry"`
    if (this.hierarchy == null) {
      return this.name;
    }
    const fragments = this.hierarchy.split(' > ').slice(1);
    return fragments.reverse().join(', ');
  }

  get type(): string {
    if (this.countryID === null) return TYPE_COUNTRY;
    if (this.regionID === null) return TYPE_REGION;
    return TYPE_CITY;
  }

  get features(): Feature[] {
    return this.shapes.map((s) => ({
      type: 'Feature',
      geometry: s.geometry,
      properties: {
        id: this.id,
      },
    }));
  }

  static fromApi(apiObject: ILocationAPI): Location {
    return new Location(
      apiObject.location_id,
      apiObject.region_id,
      apiObject.level,
      apiObject.name,
      apiObject.code,
      apiObject.country_id,
      apiObject.subregion_id,
      apiObject.children != null ? apiObject.children.map((x) => Location.fromApi(x)) : [],
      apiObject.sites != null ? apiObject.sites.map((x) => Site.fromApi(x)) : [],
      apiObject.search_match,
      apiObject.external_id,
      apiObject.shapes != null ? apiObject.shapes.map((x) => LocationShape.fromApi(x)) : [],
      apiObject.hierarchy,
      apiObject.archived,
    );
  }

  toApi(): ILocationAPI {
    return {
      location_id: this.id >= 0 ? this.id : undefined,
      region_id: this.regionID,
      level: this.level,
      name: this.name,
      code: this.code,
      country_id: this.countryID,
      subregion_id: this.subRegionID,
      external_id: this.externalID,
      shapes: this.shapes != null ? this.shapes.map((x) => x.toApi()) : [],
      hierarchy: this.hierarchy,
      archived: this.archived,
    };
  }
}
